import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, TwoUp, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSErrorHandling: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Error Handling</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    Real software isn&apos;t built around success — it&apos;s built around handling failure. Detecting, recovering from, logging, and communicating errors is what keeps systems reliable.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="20 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>On a food delivery platform, clicking <Mark>Place Order</Mark> triggers a backend workflow:</Para>
   <CodeBlock snippets={[{ language: 'text', code: `Validate user → Validate inventory → Process payment → Create order → Send notification` }]} defaultLanguage="text" id="err-flow" />
   <Para>Now imagine the payment gateway is down, the database connection is lost, the network times out, or the user data is invalid. What should happen — should the app crash, or handle the failure gracefully? Real software is built around handling failure, because failures are inevitable.</Para>
  </section>

  {/* 2. Why Error Handling Exists */}
  <section className="mb-12">
   <SectionHeading n={2}>Why Error Handling Exists</SectionHeading>
   <Para>Beginners assume good code never fails. False. Even perfect code fails due to:</Para>
   <div className="flex flex-wrap gap-2 mb-4">{['Network issues', 'Database downtime', 'External API failures', 'Hardware problems', 'Invalid user input', 'Permission issues'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <Para>Failure is normal, so failure handling is mandatory. Error handling exists to <Mark>prevent crashes, recover safely, inform users, inform developers, and maintain consistency</Mark>.</Para>
  </section>

  {/* 3. What Is An Error */}
  <section className="mb-12">
   <SectionHeading n={3}>What Is an Error?</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200">An <Mark>error</Mark> is an abnormal condition that prevents the normal execution of a program.</p>
   </div>
   <div className="flex flex-wrap gap-2">{['User not found', 'Payment failed', 'Cannot read property', 'Database timeout', 'Unauthorized access'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
  </section>

  {/* 4. Types Of Errors */}
  <section className="mb-12">
   <SectionHeading n={4}>Types of Errors</SectionHeading>
   <SubHeading>Syntax errors — code can&apos;t even start</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `if ( // SyntaxError — fails before execution begins` }]} defaultLanguage="javascript" id="err-syntax" />
   <SubHeading>Runtime errors — fail during execution</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = null;\nconsole.log(user.name); // TypeError` }]} defaultLanguage="javascript" id="err-runtime" />
   <SubHeading>Logical errors — runs fine, wrong result</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const tax = amount * 0.20; // should have been 0.02\n// no exception thrown — but the business logic is wrong` }]} defaultLanguage="javascript" id="err-logical" />
   <Para>Logical errors throw no exception, which makes them the hardest bugs to find.</Para>
  </section>

  {/* 5. JavaScript Error Object */}
  <section className="mb-12">
   <SectionHeading n={5}>The JavaScript Error Object</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const error = new Error("Payment Failed");\n\nerror.message; // "Payment Failed"\nerror.name;    // "Error"\nerror.stack;   // stack trace\n\nconsole.log(error.message);` }]} defaultLanguage="javascript" id="err-object" />
   <Output>{`Payment Failed`}</Output>
  </section>

  {/* 6. Built-In Error Types */}
  <section className="mb-12">
   <SectionHeading n={6}>Built-In Error Types</SectionHeading>
   <div className="my-4 space-y-2">
    {[
     ['Error', 'Generic error — throw new Error("Something failed")'],
     ['TypeError', 'Wrong data type — e.g. reading a property of null'],
     ['ReferenceError', 'Variable doesn\u2019t exist — using an undefined name'],
     ['SyntaxError', 'Invalid syntax — code can\u2019t be parsed'],
     ['RangeError', 'Value outside the allowed range'],
     ['URIError', 'Malformed URI operations'],
    ].map(([name, line]) => (
     <div key={name} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2.5">
      <span className="font-mono text-[13px] font-bold text-amber-700 dark:text-amber-300">{name}</span>
      <span className="text-[13px] text-gray-600 dark:text-gray-300"> — {line}</span>
     </div>
    ))}
   </div>
   <Para>Interviewers frequently ask about the differences between these error types.</Para>
  </section>

  {/* 7. The throw Keyword */}
  <section className="mb-12">
   <SectionHeading n={7}>The throw Keyword</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `function withdraw(amount) {\n  if (amount <= 0) {\n    throw new Error("Invalid Amount");\n  }\n}\n\nwithdraw(-100); // Error: Invalid Amount — execution stops immediately` }]} defaultLanguage="javascript" id="err-throw" />
  </section>

  {/* 8. Why Throw Custom Errors */}
  <section className="mb-12">
   <SectionHeading n={8}>Why Throw Custom Errors?</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `function placeOrder(user) {\n  if (!user.isVerified) {\n    throw new Error("User Must Be Verified");\n  }\n}` }]} defaultLanguage="javascript" id="err-custom-throw" />
   <Para>The system explicitly communicates a <Mark>business failure</Mark> instead of silently continuing with bad state.</Para>
  </section>

  {/* 9. try/catch */}
  <section className="mb-12">
   <SectionHeading n={9}>try/catch</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `try {\n  throw new Error("Payment Failed");\n} catch (error) {\n  console.log(error.message);\n}` }]} defaultLanguage="javascript" id="err-trycatch" />
   <Output>{`Payment Failed`}</Output>
   <Para>Instead of crashing, the error is captured.</Para>
  </section>

  {/* 10. Execution Flow Of try/catch */}
  <section className="mb-12">
   <SectionHeading n={10}>Execution Flow of try/catch</SectionHeading>
   <CodeBlock snippets={[{ language: 'text', code: `try\n ├─ success? → yes → continue (catch skipped)\n └─ success? → no  → catch executes` }]} defaultLanguage="text" id="err-flow2" />
  </section>

  {/* 11. finally Block */}
  <section className="mb-12">
   <SectionHeading n={11}>The finally Block</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `try {\n  processPayment();\n} catch (error) {\n  console.log(error);\n} finally {\n  closeConnection(); // runs whether or not payment failed\n}` }]} defaultLanguage="javascript" id="err-finally" />
   <Para>Common uses: database connections, file handles, network resources, loading indicators.</Para>
  </section>

  {/* 12. Custom Error Classes */}
  <section className="mb-12">
   <SectionHeading n={12}>Custom Error Classes</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `class PaymentError extends Error {\n  constructor(message) {\n    super(message);\n    this.name = "PaymentError";\n  }\n}\n\nthrow new PaymentError("Card Declined");` }]} defaultLanguage="javascript" id="err-class" />
   <div className="flex flex-wrap gap-2">{['Better classification', 'Better logging', 'Better recovery logic'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
  </section>

  {/* 13. Handling Different Error Types */}
  <section className="mb-12">
   <SectionHeading n={13}>Handling Different Error Types</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `try {\n  processPayment();\n} catch (error) {\n  if (error instanceof PaymentError) {\n    console.log("Payment Failed");\n  }\n}` }]} defaultLanguage="javascript" id="err-instanceof" />
   <Para>Different errors can trigger different actions — common in enterprise applications.</Para>
  </section>

  {/* 14. Promise Error Handling */}
  <section className="mb-12">
   <SectionHeading n={14}>Promise Error Handling</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `fetchUser()\n  .then(user => {\n    console.log(user);\n  })\n  .catch(error => {\n    console.log(error);\n  });` }]} defaultLanguage="javascript" id="err-promise" />
   <Para>Any failure in the chain eventually reaches <Code>.catch()</Code>.</Para>
  </section>

  {/* 15. Promise Error Propagation */}
  <section className="mb-12">
   <SectionHeading n={15}>Promise Error Propagation</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `fetchUser()\n  .then(user => {\n    throw new Error("Validation Failed");\n  })\n  .catch(error => {\n    console.log(error.message);\n  });` }]} defaultLanguage="javascript" id="err-prop" />
   <Output>{`Validation Failed`}</Output>
   <Para>Errors automatically travel through the chain to the nearest handler.</Para>
  </section>

  {/* 16. Async/Await Error Handling */}
  <section className="mb-12">
   <SectionHeading n={16}>Async/Await Error Handling</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `try {\n  const user = await getUser();\n} catch (error) {\n  console.log(error);\n}` }]} defaultLanguage="javascript" id="err-async" />
   <Para>This is essentially <Code>Promise.catch()</Code>, just with cleaner, synchronous-looking syntax.</Para>
  </section>

  {/* 17. Real Production Example */}
  <section className="mb-12">
   <SectionHeading n={17}>Real Production Example</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `async function placeOrder() {\n  try {\n    const payment = await processPayment();\n    return payment;\n  } catch (error) {\n    logger.error(error);\n    throw error; // log, then re-throw\n  }\n}` }]} defaultLanguage="javascript" id="err-prod" />
   <Para>Notice the pattern: log the error, then re-throw it.</Para>
  </section>

  {/* 18. Why Re-Throw Errors */}
  <section className="mb-12">
   <SectionHeading n={18}>Why Re-Throw Errors?</SectionHeading>
   <TwoUp
    left={{ title: '❌ Swallow it', items: ['catch(e) { console.log(e); }', 'Failure is hidden', 'Caller thinks it worked'] }}
    right={{ title: '✅ Log then re-throw', items: ['logger.error(e); throw e;', 'Failure stays visible', 'Higher layers decide what to do'] }}
   />
  </section>

  {/* 19. Error Propagation */}
  <section className="mb-12">
   <SectionHeading n={19}>Error Propagation</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `async function A() { await B(); }\nasync function B() { await C(); }\nasync function C() { throw new Error("Failed"); }` }]} defaultLanguage="javascript" id="err-bubble" />
   <CodeBlock snippets={[{ language: 'text', code: `C fails → B receives error → A receives error → top-level handler` }]} defaultLanguage="text" id="err-bubble-flow" />
   <Para>Errors naturally bubble upward — this is error propagation.</Para>
  </section>

  {/* 20. Operational vs Programmer Errors */}
  <section className="mb-12">
   <SectionHeading n={20}>Operational Errors vs Programmer Errors</SectionHeading>
   <TwoUp
    left={{ title: 'Operational (expected)', items: ['Payment declined', 'Network timeout', 'User not found', 'Database down', 'Rate limit exceeded', '→ handle gracefully'] }}
    right={{ title: 'Programmer (bugs)', items: ['Undefined variable', 'Null reference', 'Wrong logic', 'Memory leak', '→ require code fixes'] }}
   />
   <Para>A popular Node.js distinction: operational errors are recovered at runtime; programmer errors are fixed in code.</Para>
  </section>

  {/* 21. Global Error Handling */}
  <section className="mb-12">
   <SectionHeading n={21}>Global Error Handling</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `// Browser\nwindow.onerror = function (error) {\n  console.log(error);\n};\n\n// Node.js\nprocess.on("uncaughtException", error => {\n  console.log(error);\n});` }]} defaultLanguage="javascript" id="err-global" />
   <Para>Top-level handlers capture failures that escape normal handling.</Para>
  </section>

  {/* 22. Unhandled Promise Rejections */}
  <section className="mb-12">
   <SectionHeading n={22}>Unhandled Promise Rejections</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `Promise.reject(new Error("Failed")); // no .catch() → unhandled rejection\n\n// Node.js safeguard\nprocess.on("unhandledRejection", error => {\n  console.log(error);\n});` }]} defaultLanguage="javascript" id="err-unhandled" />
   <Para>Without a <Code>.catch()</Code> the rejection is unhandled — modern runtimes warn or terminate the process.</Para>
  </section>

  {/* 23. Logging Errors */}
  <section className="mb-12">
   <SectionHeading n={23}>Logging Errors</SectionHeading>
   <Callout tone="warning" title="The worst pattern">An empty <Code>catch(error) {'{}'}</Code> swallows the failure silently. Always log.</Callout>
   <CodeBlock snippets={[{ language: 'javascript', code: `logger.error("Payment Failed", { orderId, userId, error });` }]} defaultLanguage="javascript" id="err-log" />
   <Para>Structured logs are what let you debug production incidents.</Para>
  </section>

  {/* 24. User-Friendly Error Messages */}
  <section className="mb-12">
   <SectionHeading n={24}>User-Friendly Error Messages</SectionHeading>
   <TwoUp
    left={{ title: '❌ For the user', items: ['"TypeError: Cannot read property"', 'Technical, scary, useless'] }}
    right={{ title: '✅ For the user', items: ['"Unable to process payment. Please try again."', 'Clear and actionable'] }}
   />
   <Para>Users need helpful messages; developers need detailed logs. Keep them separate.</Para>
  </section>

  {/* 25. Retryable vs Non-Retryable */}
  <section className="mb-12">
   <SectionHeading n={25}>Retryable vs Non-Retryable Errors</SectionHeading>
   <TwoUp
    left={{ title: 'Retryable', items: ['Network timeout', 'Temporary service outage', '→ retry may help'] }}
    right={{ title: 'Non-retryable', items: ['Invalid password', 'Invalid card number', '→ retrying is useless'] }}
   />
   <Para>This distinction is important in distributed systems and retry logic.</Para>
  </section>

  {/* 26. TypeScript Error Handling */}
  <section className="mb-12">
   <SectionHeading n={26}>TypeScript Error Handling</SectionHeading>
   <CodeBlock snippets={[{ language: 'typescript', code: `try {\n  const user = await getUser();\n} catch (error: unknown) {\n  if (error instanceof Error) {\n    console.log(error.message);\n  }\n}` }]} defaultLanguage="typescript" id="err-ts" />
   <Para>TypeScript types caught errors as <Code>unknown</Code>, encouraging a safe <Code>instanceof</Code> check before use.</Para>
  </section>

  {/* 27. Java Comparison */}
  <section className="mb-12">
   <SectionHeading n={27}>Java Comparison</SectionHeading>
   <CodeBlock snippets={[{ language: 'java', code: `try {\n} catch (Exception e) {\n} finally {\n}` }]} defaultLanguage="java" id="err-java" />
   <Para>Similar concepts, but JavaScript adds promise errors, async errors, and event-loop considerations that create extra complexity.</Para>
  </section>

  {/* 28. Common Production Mistakes */}
  <section className="mb-12">
   <SectionHeading n={28}>Common Production Mistakes</SectionHeading>
   <div className="flex flex-wrap gap-2">{['Swallowing errors (empty catch)', 'Not logging errors', 'Exposing internal errors to users', 'Treating all errors the same', 'Missing async error handling', 'Ignoring promise rejections'].map((e) => <Pill key={e}>❌ {e}</Pill>)}</div>
  </section>

  {/* 29. Common Interview Questions */}
  <section className="mb-12">
   <SectionHeading n={29}>Common Interview Questions</SectionHeading>
   <div className="flex flex-wrap gap-2 mb-5">
    {['throw vs return?', 'try/catch vs Promise.catch()?', 'What is error propagation?', 'What is re-throwing?', 'Custom errors?', 'Operational vs programmer errors?', 'Why use finally?', 'Unhandled promise rejection?', 'How do large systems handle errors?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
   <QA q="Why log and re-throw instead of just catching?">
    Catching and swallowing hides the failure — the caller assumes success. Logging records it for debugging, and re-throwing lets higher layers decide how to respond (retry, fallback, user message), preserving correct control flow.
   </QA>
   <QA q="What is the difference between operational and programmer errors?">
    Operational errors are expected runtime failures (network timeout, payment declined) that should be handled gracefully. Programmer errors are bugs (null reference, wrong logic) that need code fixes, not runtime recovery.
   </QA>
   <QA q="How does error handling differ between sync, promises, and async/await?">
    Synchronous code uses <Code>try/catch</Code>; promise chains use <Code>.catch()</Code>; async/await uses <Code>try/catch</Code> around <Code>await</Code>. All three let errors propagate to the nearest handler — the difference is mostly syntax.
   </QA>
  </section>

  {/* 30. Mental Model */}
  <section className="mb-12">
   <SectionHeading n={30}>Mental Model</SectionHeading>
   <Para>Think of error handling like <Mark>car safety systems</Mark>. The goal isn&apos;t to prevent every accident — it&apos;s to survive accidents safely. Good software follows the same philosophy.</Para>
  </section>

  {/* 31. Interview One-Liner */}
  <section className="mb-12">
   <SectionHeading n={31}>Interview One-Liner</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4">
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Error handling is the process of detecting, managing, recovering from, and reporting failures so applications remain reliable, observable, and user-friendly even when things go wrong.
    </p>
   </div>
  </section>

  {/* 32. Most Important Takeaway */}
  <section className="mb-4">
   <SectionHeading n={32}>Most Important Takeaway</SectionHeading>
   <Callout tone="info" title="Takeaway">
    Failures are inevitable. Strong engineers don&apos;t build systems that never fail — they build systems that fail safely, recover intelligently, log meaningfully, communicate clearly, and maintain data consistency even during unexpected situations.
   </Callout>
  </section>
 </div>
);

export default JSErrorHandling;
